// CareWay — shared front-end behavior.
//
// This file grows as more pages are built (see docs/shopify-migration-plan.md).
// Right now it only contains what the header/footer/homepage need: the mega-menu
// and the mobile nav toggle. Both are the same logic ported unchanged from the
// approved static prototype's js/main.js — nothing about this interactivity
// needed to change for Shopify.

document.addEventListener('DOMContentLoaded', () => {
  initMegaMenu();
  initMobileNav();
  initMomentsCarousel();
  initJournalEmptyPills();
  initWishlist();
  initEmailPopup();
  initAnnouncementBar();
  initFixedHeader();
  initCartDrawer();
});

// Mega-menu: only one <details> nav item open at a time, closes on outside click / Escape
function initMegaMenu() {
  const items = document.querySelectorAll('.main-nav > .nav-item');
  if (!items.length) return;

  items.forEach((item) => {
    item.addEventListener('toggle', () => {
      if (item.open) {
        items.forEach((other) => { if (other !== item) other.open = false; });
      }
    });
  });

  document.addEventListener('click', (e) => {
    if (!e.target.closest('.main-nav')) {
      items.forEach((item) => { item.open = false; });
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      items.forEach((item) => { item.open = false; });
    }
  });
}

// Mobile nav: hamburger toggles the collapsed nav panel
function initMobileNav() {
  const toggle = document.querySelector('.mobile-nav-toggle');
  const nav = document.querySelector('.main-nav');
  if (!toggle || !nav) return;

  toggle.addEventListener('click', () => {
    const isOpen = nav.classList.toggle('nav-open');
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });
}

// Moments strip: real scroll-snap carousel. Cards fade/rise in as they enter
// view, and dot pagination (built dynamically, since how many cards fit per
// "page" depends on viewport width) scrolls smoothly between pages. If every
// card already fits without overflow, no dots are shown at all.
function initMomentsCarousel() {
  document.querySelectorAll('[data-moments-track]').forEach((track) => {
    const items = Array.from(track.querySelectorAll('[data-moments-item]'));
    if (!items.length) return;

    // Items are fully visible by default (no JS = no animation, never hidden
    // content). Only opt a card into the "fade/rise in" treatment once we
    // know IntersectionObserver is actually available to reveal it again.
    if ('IntersectionObserver' in window) {
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('reveal-pending');
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.25 });

      items.forEach((item) => {
        item.classList.add('reveal-pending');
        revealObserver.observe(item);
        // Safety net: if the observer never fires (e.g. this browser pane's
        // rendering quirks, or any other edge case), never leave a real
        // photo permanently invisible.
        setTimeout(() => {
          item.classList.remove('reveal-pending');
          item.classList.add('is-visible');
        }, 2000);
      });
    }

    const dotsWrap = track.parentElement.querySelector('[data-moments-dots]');
    if (!dotsWrap) return;

    let dots = [];

    function buildDots() {
      const itemWidth = items[0].getBoundingClientRect().width + 28;
      const perView = Math.max(1, Math.round(track.clientWidth / itemWidth));
      const pageCount = Math.max(1, Math.ceil(items.length / perView));

      dotsWrap.innerHTML = '';
      dots = [];

      if (pageCount <= 1) {
        dotsWrap.style.display = 'none';
        return;
      }
      dotsWrap.style.display = '';

      for (let i = 0; i < pageCount; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        if (i === 0) dot.classList.add('active');
        dot.addEventListener('click', () => {
          track.scrollTo({ left: i * track.clientWidth, behavior: 'smooth' });
        });
        dotsWrap.appendChild(dot);
        dots.push(dot);
      }
    }

    function updateActiveDot() {
      if (!dots.length || !track.clientWidth) return;
      const index = Math.round(track.scrollLeft / track.clientWidth);
      dots.forEach((dot, i) => dot.classList.toggle('active', i === index));
    }

    let scrollTimer;
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(updateActiveDot, 80);
    });

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { buildDots(); updateActiveDot(); }, 150);
    });

    buildDots();
  });
}

// Journal category pills for a CareWay taxonomy tag that currently has zero
// articles render as <button data-journal-empty-pill> instead of a link,
// because Shopify's native /tagged/<tag> route 404s for a tag no article has
// ever used. Clicking one swaps in an empty-state message in place — no
// navigation, no fake article, no 404.
function initJournalEmptyPills() {
  const pillsWrap = document.querySelector('[data-journal-pills]');
  const featured = document.querySelector('[data-journal-featured]');
  const content = document.querySelector('[data-journal-content]');
  const emptyState = document.querySelector('[data-journal-empty-state]');
  const heading = document.querySelector('[data-journal-heading] h1');
  if (!pillsWrap || !content || !emptyState) return;

  pillsWrap.querySelectorAll('[data-journal-empty-pill]').forEach((btn) => {
    btn.addEventListener('click', () => {
      pillsWrap.querySelectorAll('.pill').forEach((p) => p.classList.remove('active'));
      btn.classList.add('active');
      if (featured) featured.hidden = true;
      content.hidden = true;
      emptyState.hidden = false;
      if (heading) heading.textContent = btn.dataset.tag;
    });
  });
}

// Wishlist ("Saved Items"): no app, no account — saved to the shopper's own
// browser via localStorage. Per-device only; there's no server-side record,
// so this never shows a fabricated count or a fake "saved" state on load.
const WISHLIST_KEY = 'careway_wishlist';

function getWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function setWishlist(items) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
  } catch (e) {
    // Storage unavailable (private browsing, quota, etc.) — fail silently,
    // the button just won't persist across reloads.
  }
}

function initWishlist() {
  refreshWishlistUI();

  document.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('[data-wishlist-remove]');
    if (removeBtn) {
      e.preventDefault();
      const card = removeBtn.closest('[data-product-id]');
      if (!card) return;
      const items = getWishlist().filter((item) => item.id !== card.dataset.productId);
      setWishlist(items);
      refreshWishlistUI();
      return;
    }

    const toggleBtn = e.target.closest('[data-wishlist-toggle]');
    if (toggleBtn) {
      e.preventDefault();
      e.stopPropagation();
      const id = toggleBtn.dataset.productId;
      const items = getWishlist();
      const existingIndex = items.findIndex((item) => item.id === id);
      if (existingIndex > -1) {
        items.splice(existingIndex, 1);
      } else {
        items.push({
          id,
          handle: toggleBtn.dataset.productHandle,
          title: toggleBtn.dataset.productTitle,
          price: toggleBtn.dataset.productPrice,
          image: toggleBtn.dataset.productImage,
          url: toggleBtn.dataset.productUrl,
        });
      }
      setWishlist(items);
      refreshWishlistUI();
    }
  });
}

function refreshWishlistUI() {
  const items = getWishlist();
  const savedIds = new Set(items.map((item) => item.id));

  document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
    btn.classList.toggle('is-active', savedIds.has(btn.dataset.productId));
  });

  const countBadge = document.querySelector('[data-wishlist-count]');
  if (countBadge) {
    countBadge.textContent = items.length;
    countBadge.hidden = items.length === 0;
  }

  renderSavedItemsGrid(items);
}

function renderSavedItemsGrid(items) {
  const grid = document.querySelector('[data-wishlist-grid]');
  const template = document.querySelector('[data-wishlist-card-template]');
  const emptyMessage = document.querySelector('[data-wishlist-empty]');
  if (!grid || !template) return;

  grid.innerHTML = '';
  if (emptyMessage) emptyMessage.hidden = items.length > 0;

  items.forEach((item) => {
    const card = template.content.firstElementChild.cloneNode(true);
    card.href = item.url || '#';
    card.dataset.productId = item.id;
    const img = card.querySelector('img');
    if (img) {
      img.src = item.image || '';
      img.alt = item.title || '';
    }
    const title = card.querySelector('[data-wishlist-card-title]');
    if (title) title.textContent = item.title || '';
    const price = card.querySelector('[data-wishlist-card-price]');
    if (price) price.textContent = item.price || '';
    const removeBtn = card.querySelector('[data-wishlist-remove]');
    if (removeBtn) removeBtn.classList.add('is-active');
    grid.appendChild(card);
  });
}

// Note: the newsletter form (sections/newsletter.liquid) needs no JS at all —
// it's a real Shopify {% form 'customer' %}, and the success/error state is
// rendered server-side via the `form` object on the reloaded page.

// Email popup (sections/email-popup.liquid): a second, independent real
// Shopify customer form (same mechanism as the newsletter one above, tagged
// "popup" so signups from it are distinguishable in Admin). Submitted via
// fetch so the popup can show a success state without a full page reload;
// the fetched HTML is parsed for the same server-rendered
// [data-email-popup-result] marker the no-JS fallback already relies on, so
// success/failure always comes from Shopify's own form result — never
// guessed client-side. Delay, dismissal, and permanent-after-subscribe
// timing are read from data-* attributes set by the section's Theme Editor
// settings, and persisted with localStorage/sessionStorage (never shown
// twice in the same session, never shown again on this device once someone
// actually subscribes).
function initEmailPopup() {
  const root = document.querySelector('[data-email-popup]');
  if (!root) return;

  const DISMISSED_KEY = 'careway_popup_dismissed_until';
  const SUBSCRIBED_KEY = 'careway_popup_subscribed';
  const SHOWN_SESSION_KEY = 'careway_popup_shown_session';
  const ADD_TO_CART_QUIET_MS = 15000;

  const dialog = root.querySelector('[data-email-popup-dialog]');
  const form = root.querySelector('.email-popup-form');
  const resultEl = root.querySelector('[data-email-popup-result]');
  const formState = root.querySelector('[data-email-popup-form-state]');
  const successState = root.querySelector('[data-email-popup-success]');
  const emailInput = root.querySelector('[data-email-popup-input]');
  const fieldError = root.querySelector('[data-email-popup-field-error]');
  const submitBtn = root.querySelector('[data-email-popup-submit]');
  const submitLabel = root.querySelector('[data-email-popup-submit-label]');
  const copyBtn = root.querySelector('[data-email-popup-copy]');
  const codeEl = root.querySelector('[data-email-popup-code]');
  const copyConfirm = root.querySelector('[data-email-popup-copy-confirm]');
  const copyLabel = root.querySelector('[data-email-popup-copy-label]');

  let lastFocusedElement = null;
  let addedToCartAt = 0;

  function safeGet(storage, key) {
    try { return storage.getItem(key); } catch (e) { return null; }
  }
  function safeSet(storage, key, value) {
    try { storage.setItem(key, value); } catch (e) { /* private mode / quota - fail silently */ }
  }

  function isSubscribed() { return safeGet(localStorage, SUBSCRIBED_KEY) === 'true'; }
  function markSubscribed() { safeSet(localStorage, SUBSCRIBED_KEY, 'true'); }
  function dismissedUntil() { return parseInt(safeGet(localStorage, DISMISSED_KEY) || '0', 10); }
  function markDismissed(days) {
    safeSet(localStorage, DISMISSED_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
  }
  function shownThisSession() { return safeGet(sessionStorage, SHOWN_SESSION_KEY) === 'true'; }
  function markShownThisSession() { safeSet(sessionStorage, SHOWN_SESSION_KEY, 'true'); }

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    const focusable = dialog.querySelectorAll('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape') closePopup();
  }

  function openPopup() {
    if (!root.hidden) return;
    lastFocusedElement = document.activeElement;
    root.hidden = false;
    document.body.classList.add('email-popup-open');
    // A brief setTimeout (rather than requestAnimationFrame) so the
    // hidden -> visible attribute change is committed before the
    // transition-triggering class is added, even if this fires while the
    // tab is backgrounded - rAF callbacks can stay suspended indefinitely
    // in that case, but timers still run (just throttled).
    setTimeout(() => root.classList.add('is-visible'), 20);
    const closeBtn = root.querySelector('[data-email-popup-close]');
    if (closeBtn) closeBtn.focus();
    document.addEventListener('keydown', onKeydown);
    dialog.addEventListener('keydown', trapFocus);
    markShownThisSession();
  }

  function closePopup(options) {
    const remember = !options || options.remember !== false;
    root.classList.remove('is-visible');
    document.body.classList.remove('email-popup-open');
    document.removeEventListener('keydown', onKeydown);
    dialog.removeEventListener('keydown', trapFocus);

    const finish = () => { root.hidden = true; };
    if (reducedMotion()) {
      finish();
    } else {
      setTimeout(finish, 250);
    }

    if (remember && !isSubscribed()) {
      markDismissed(parseInt(root.dataset.dismissDays, 10) || 7);
    }
    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  root.querySelectorAll('[data-email-popup-close]').forEach((btn) => {
    btn.addEventListener('click', () => closePopup());
  });
  root.addEventListener('click', (e) => {
    if (e.target === root) closePopup();
  });

  // Never interrupt someone mid Add to Cart - if they clicked it recently,
  // hold off a little longer rather than opening on top of that moment.
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-add-to-cart]')) {
      addedToCartAt = Date.now();
    }
  });

  function shouldShow() {
    if (isSubscribed()) return false;
    if (shownThisSession()) return false;
    if (Date.now() < dismissedUntil()) return false;
    return true;
  }

  function scheduleShow() {
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const delaySeconds = parseInt(isMobile ? root.dataset.delayMobile : root.dataset.delayDesktop, 10) || 10;
    setTimeout(() => {
      if (!shouldShow()) return;
      if (Date.now() - addedToCartAt < ADD_TO_CART_QUIET_MS) {
        setTimeout(() => { if (shouldShow()) openPopup(); }, 5000);
        return;
      }
      openPopup();
    }, delaySeconds * 1000);
  }

  function showSuccess() {
    if (formState) formState.hidden = true;
    if (successState) successState.hidden = false;
  }

  function showFieldError(message) {
    if (!fieldError) return;
    fieldError.textContent = message;
    fieldError.hidden = false;
    if (emailInput) emailInput.setAttribute('aria-invalid', 'true');
  }

  function clearFieldError() {
    if (!fieldError) return;
    fieldError.hidden = true;
    fieldError.textContent = '';
    if (emailInput) emailInput.removeAttribute('aria-invalid');
  }

  // A page load carrying a real server-rendered result means the shopper
  // just submitted this exact form without JS (or JS failed) and got
  // redirected back - respect that result and open straight into it,
  // instead of silently discarding what Shopify already told us.
  if (resultEl && resultEl.dataset.success === 'true') {
    markSubscribed();
    showSuccess();
    openPopup();
  } else if (resultEl && resultEl.dataset.error) {
    showFieldError(resultEl.dataset.error);
    openPopup();
  } else {
    scheduleShow();
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  // Shopify's own bot-protection for customer forms can, after its own
  // async check, submit the form for real (a full page navigation) instead
  // of leaving it to us - independent of this handler's preventDefault().
  // When that happens mid-flight, our fetch gets cut off by the navigation
  // and would otherwise flash a false "something went wrong" right before
  // the real (successful) reloaded page appears. Once a real unload starts,
  // stop updating this popup's UI entirely and let that reload show the
  // true result instead.
  let pageUnloading = false;
  window.addEventListener('pagehide', () => { pageUnloading = true; });

  if (form) {
    const originalSubmitLabel = submitLabel ? submitLabel.textContent : '';

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      clearFieldError();

      const emailValue = emailInput ? emailInput.value.trim() : '';
      if (!emailValue || !isValidEmail(emailValue)) {
        showFieldError('Please enter a valid email address.');
        if (emailInput) emailInput.focus();
        return;
      }

      if (submitBtn) submitBtn.disabled = true;
      if (submitLabel) submitLabel.textContent = 'Sending…';

      const formData = new FormData(form);
      fetch(form.action, { method: 'POST', body: formData, headers: { Accept: 'text/html' } })
        .then((res) => res.text())
        .then((html) => {
          if (pageUnloading) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const result = doc.querySelector('[data-email-popup-result]');

          if (result && result.dataset.success === 'true') {
            markSubscribed();
            showSuccess();
          } else if (result && result.dataset.error) {
            showFieldError(result.dataset.error);
          } else {
            // Couldn't confirm a real result from Shopify - don't claim
            // success on a guess, ask them to try again.
            showFieldError('Something went wrong — please try again.');
          }
        })
        .catch(() => {
          if (pageUnloading) return;
          showFieldError('Something went wrong — please try again.');
        })
        .finally(() => {
          if (pageUnloading) return;
          if (submitBtn) submitBtn.disabled = false;
          if (submitLabel) submitLabel.textContent = originalSubmitLabel;
        });
    });
  }

  function fallbackCopy(text, done) {
    const temp = document.createElement('textarea');
    temp.value = text;
    temp.style.position = 'fixed';
    temp.style.opacity = '0';
    document.body.appendChild(temp);
    temp.select();
    try { document.execCommand('copy'); done(); } catch (e) { /* clipboard unavailable - button just won't confirm */ }
    document.body.removeChild(temp);
  }

  if (copyBtn && codeEl) {
    copyBtn.addEventListener('click', () => {
      const code = codeEl.textContent.trim();
      const done = () => {
        if (copyConfirm) {
          copyConfirm.hidden = false;
          setTimeout(() => { copyConfirm.hidden = true; }, 2500);
        }
        if (copyLabel) {
          const original = copyLabel.textContent;
          copyLabel.textContent = 'Copied';
          setTimeout(() => { copyLabel.textContent = original; }, 2500);
        }
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(() => fallbackCopy(code, done));
      } else {
        fallbackCopy(code, done);
      }
    });
  }
}

// Announcement bar: click the discount code to copy it, label briefly
// changes to "Copied" then reverts. Same copy/fallback pattern as the email
// popup's code button, kept self-contained since this one lives outside it.
function initAnnouncementBar() {
  const btn = document.querySelector('.announcement-bar-code[data-copy-code]');
  if (!btn) return;

  function fallbackCopy(text, done) {
    const temp = document.createElement('textarea');
    temp.value = text;
    temp.style.position = 'fixed';
    temp.style.opacity = '0';
    document.body.appendChild(temp);
    temp.select();
    try { document.execCommand('copy'); done(); } catch (e) { /* clipboard unavailable - button just won't confirm */ }
    document.body.removeChild(temp);
  }

  btn.addEventListener('click', () => {
    const code = btn.getAttribute('data-copy-code');
    const originalLabel = btn.getAttribute('data-default-label') || btn.textContent;
    const done = () => {
      btn.textContent = 'Copied';
      btn.classList.add('is-copied');
      setTimeout(() => {
        btn.textContent = originalLabel;
        btn.classList.remove('is-copied');
      }, 1800);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(() => fallbackCopy(code, done));
    } else {
      fallbackCopy(code, done);
    }
  });
}

// Fixed header on scroll. Real-world testing found the header's native
// `position: sticky` unreliable in practice, so this pins it with
// `position: fixed` once the page has scrolled past the announcement bar,
// via a small class toggle — never the announcement bar itself, which
// always scrolls away normally. A spacer sibling takes over the header's
// document-flow space the instant it goes fixed, so nothing else on the
// page jumps. The engage/disengage threshold is read fresh from the
// announcement bar's live position every check (not cached), so it stays
// correct across resize/orientation change without a separate recalculation
// path, and handles the bar being absent (threshold 0) or disabled.
//
// Deliberately NOT requestAnimationFrame-throttled: an rAF callback queued
// while the tab/frame is backgrounded can be deferred indefinitely by the
// browser, and a naive "ticking" guard around it then never resets, silently
// wedging the handler so no further scroll ever engages/disengages the
// header. The check itself is one getBoundingClientRect() and a comparison —
// cheap enough to run directly on every scroll event with no throttling.
function initFixedHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const bar = document.querySelector('.announcement-bar');

  const spacer = document.createElement('div');
  spacer.className = 'site-header-spacer';
  header.parentNode.insertBefore(spacer, header.nextSibling);

  let isFixed = false;

  function thresholdY() {
    return bar ? bar.getBoundingClientRect().bottom + window.scrollY : 0;
  }

  function engage() {
    spacer.style.height = header.offsetHeight + 'px';
    header.classList.add('site-header-fixed');
    isFixed = true;
  }

  function disengage() {
    header.classList.remove('site-header-fixed');
    spacer.style.height = '0px';
    isFixed = false;
  }

  function update() {
    if (!isFixed && window.scrollY >= thresholdY()) {
      engage();
    } else if (isFixed && window.scrollY < thresholdY()) {
      disengage();
    }
  }

  function onResize() {
    if (isFixed) spacer.style.height = header.offsetHeight + 'px';
  }

  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', onResize);
  update(); // handle loading the page already scrolled (e.g. back/forward cache)
}

// Right-side cart drawer. One instance, rendered globally by
// sections/cart-drawer.liquid. Every cart mutation re-renders that
// section's real Liquid markup server-side (via the `sections` param on
// Shopify's cart AJAX endpoints) and swaps the returned HTML in — this
// function never computes a price, variant label, or subtotal itself.
// Exposes window.CareWayCartDrawer so assets/product.js (Add to Cart) can
// open it once Shopify confirms a successful add.
function initCartDrawer() {
  const overlay = document.querySelector('[data-cart-drawer]');
  if (!overlay) return;

  const dialog = overlay.querySelector('[data-cart-drawer-dialog]');
  const closeBtn = overlay.querySelector('[data-cart-drawer-close]');

  let lastFocusedElement = null;
  let busy = false; // guards against double-click / overlapping quantity requests

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function trapFocus(e) {
    if (e.key !== 'Tab') return;
    const focusable = dialog.querySelectorAll('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function onKeydown(e) {
    if (e.key === 'Escape') close();
  }

  function open(triggerEl) {
    lastFocusedElement = triggerEl || document.activeElement;
    overlay.hidden = false;
    document.body.classList.add('cart-drawer-open');
    // Same reasoning as initEmailPopup: commit hidden->visible before the
    // transition-triggering class, using a timer (not rAF) so this still
    // fires correctly even if the tab is backgrounded.
    setTimeout(() => overlay.classList.add('is-visible'), 20);
    if (closeBtn) closeBtn.focus();
    document.addEventListener('keydown', onKeydown);
    dialog.addEventListener('keydown', trapFocus);
  }

  function close() {
    overlay.classList.remove('is-visible');
    document.body.classList.remove('cart-drawer-open');
    document.removeEventListener('keydown', onKeydown);
    dialog.removeEventListener('keydown', trapFocus);

    const finish = () => { overlay.hidden = true; };
    if (reducedMotion()) finish();
    else setTimeout(finish, 320);

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  // Swaps in a freshly server-rendered copy of this same section (heading +
  // content) without touching the overlay/dialog wrapper, so open drawers
  // update in place and the click-delegation listener below (bound to the
  // dialog, never replaced) keeps working after every refresh.
  function renderContent(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newHeading = doc.querySelector('[data-cart-drawer-heading]');
    const newContent = doc.querySelector('[data-cart-drawer-content]');
    const heading = overlay.querySelector('[data-cart-drawer-heading]');
    const content = overlay.querySelector('[data-cart-drawer-content]');
    if (newHeading && heading) heading.innerHTML = newHeading.innerHTML;
    if (newContent && content) content.innerHTML = newContent.innerHTML;
  }

  function renderAndOpen(html, triggerEl) {
    renderContent(html);
    open(triggerEl);
  }

  function updateHeaderCartCount(cart) {
    // [data-cart-count], not the shared `.cart-count` class — see the
    // matching note in assets/product.js's updateCartCount().
    const badge = document.querySelector('[data-cart-count]');
    const cartIcon = document.querySelector('a.icon-btn[href*="/cart"]');
    if (cart.item_count > 0) {
      if (badge) {
        badge.textContent = cart.item_count;
      } else if (cartIcon) {
        const span = document.createElement('span');
        span.className = 'cart-count';
        span.setAttribute('data-cart-count', '');
        span.textContent = cart.item_count;
        cartIcon.appendChild(span);
      }
    } else if (badge) {
      badge.remove();
    }
  }

  function changeQuantity(line, newQty) {
    if (busy) return; // one request in flight at a time — prevents race conditions
    busy = true;
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: newQty, sections: ['cart-drawer'] })
    })
      .then((res) => res.json())
      .then((cart) => {
        if (cart.sections && cart.sections['cart-drawer']) renderContent(cart.sections['cart-drawer']);
        updateHeaderCartCount(cart);
      })
      .catch(() => { /* leave the drawer as-is; it'll reflect the real cart next time it opens */ })
      .finally(() => { busy = false; });
  }

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();

    if (e.target.closest('[data-cart-drawer-close]')) {
      close();
      return;
    }

    const decrease = e.target.closest('[data-drawer-qty-decrease]');
    const increase = e.target.closest('[data-drawer-qty-increase]');
    const remove = e.target.closest('[data-drawer-remove]');
    if (!decrease && !increase && !remove) return;

    const row = e.target.closest('.drawer-item');
    if (!row) return;
    const line = row.dataset.drawerLine;

    if (remove) {
      changeQuantity(line, 0);
      return;
    }
    const valueEl = row.querySelector('[data-drawer-qty-value]');
    const currentQty = parseInt(valueEl ? valueEl.textContent : '1', 10) || 1;
    const delta = increase ? 1 : -1;
    // Same floor as the /cart page's own quantity stepper (assets/cart.js):
    // the -/+ buttons never go below 1; reaching zero only happens via the
    // explicit Remove action above.
    const newQty = Math.max(1, currentQty + delta);
    changeQuantity(line, newQty);
  });

  // Header cart icon opens this same drawer instead of navigating straight
  // to /cart. The real href stays on the link (progressive enhancement —
  // if this script fails to load or the fetch below fails, the click just
  // navigates to /cart normally).
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-cart-drawer-trigger]');
    if (!trigger) return;
    e.preventDefault();
    fetch(`${window.location.pathname}?sections=cart-drawer`, { headers: { 'Accept': 'application/json' } })
      .then((res) => res.json())
      .then((data) => { renderAndOpen(data['cart-drawer'], trigger); })
      .catch(() => { window.location.href = trigger.href; });
  });

  window.CareWayCartDrawer = { open, close, renderContent, renderAndOpen };
}
